"use client";

// Theme provider for the v2 two-theme system. Exposes the active theme name,
// the matching token + font set from design-tokens.ts, and a setter.
//
// Defaults to dark. Callers can pass initialTheme to force a starting state;
// setTheme persists the user's override in a cookie for the next server render.
//
// useTheme() falls back to dark when no provider is mounted, so isolated
// components (storybook, tests, ad-hoc dev rendering) don't need to wrap.

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import {
  DARK_FONTS,
  DARK_TOKENS,
  LIGHT_FONTS,
  LIGHT_TOKENS,
  type ThemeFonts,
} from "@/lib/platform/design-tokens";

export type Theme = "dark" | "light";

const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function persistThemeOverride(theme: Theme) {
  if (typeof document === "undefined") return;

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `theme=${theme}; Path=/; Max-Age=${THEME_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

export type ThemeTokens = typeof DARK_TOKENS | typeof LIGHT_TOKENS;

export interface ThemeContextValue {
  readonly theme: Theme;
  readonly tokens: ThemeTokens;
  readonly fonts: ThemeFonts;
  readonly setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
  initialTheme = "dark",
}: {
  children: ReactNode;
  initialTheme?: Theme;
}) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);
  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    persistThemeOverride(next);
  }, []);

  const value: ThemeContextValue = {
    theme,
    tokens: theme === "dark" ? DARK_TOKENS : LIGHT_TOKENS,
    fonts: theme === "dark" ? DARK_FONTS : LIGHT_FONTS,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  // Fallback: default to dark when no provider is mounted. Lets isolated
  // components render without ceremony.
  return {
    theme: "dark",
    tokens: DARK_TOKENS,
    fonts: DARK_FONTS,
    setTheme: () => {},
  };
}
